import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { formatDisplayTime } from "../lib/display-time";
import {
  CHANNEL_ANNOUNCE_ONLY_NOTE,
  EMPTY_CHANNEL_ANNOUNCEMENT_DRAFT,
  postChannelMessage,
  validateChannelAnnouncement,
  type Channel,
  type ChannelAnnouncementDraft,
  type ChannelMessage,
  type ChannelPostsLoad,
} from "../data/channels";

// One board, read and written from the console. Posts are reverse chronological messages from
// list_channel_messages / post_channel_message (#207), not Feed posts.

export function ChannelThread({
  client,
  accountId,
  channel,
  load,
  status,
  error,
  canManage,
  timeZone,
  onPosted,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly channel: Channel;
  readonly load: ChannelPostsLoad | null;
  readonly status: "loading" | "ready" | "error";
  readonly error: string | null;
  readonly canManage: boolean;
  readonly timeZone: string;
  readonly onPosted: () => void;
}) {
  return (
    <div className="channel-thread">
      <header className="channel-thread__head">
        <h3 className="channel-thread__title">{channel.name}</h3>
        <p className="channel-thread__meta">Announcements · {channel.kind.replace("_", " ")}</p>
      </header>

      {canManage ? (
        <ChannelComposer
          client={client}
          accountId={accountId}
          channel={channel}
          onPosted={onPosted}
        />
      ) : (
        <p className="channel-thread__readonly" role="status">
          {CHANNEL_ANNOUNCE_ONLY_NOTE}
        </p>
      )}

      <ChannelPosts load={load} status={status} error={error} timeZone={timeZone} />
    </div>
  );
}

function ChannelPosts({
  load,
  status,
  error,
  timeZone,
}: {
  readonly load: ChannelPostsLoad | null;
  readonly status: "loading" | "ready" | "error";
  readonly error: string | null;
  readonly timeZone: string;
}) {
  if (status === "loading" && load === null) {
    return (
      <p className="surface__state" role="status">
        Loading this channel...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load this channel."}
      </p>
    );
  }
  if (load === null) return null;

  if (load.status === "unavailable" || load.status === "failed") {
    return (
      <p
        className={load.status === "failed" ? "surface__state surface__state--error" : "surface__state"}
        role={load.status === "failed" ? "alert" : "status"}
      >
        {load.reason}
      </p>
    );
  }

  if (load.posts.length === 0) {
    return (
      <p className="surface__state" role="status">
        No posts yet.
      </p>
    );
  }

  return (
    <ul className="channel-posts" aria-label="Channel posts">
      {load.posts.map((post) => (
        <li key={post.messageId}>
          <ChannelPostRow post={post} timeZone={timeZone} />
        </li>
      ))}
    </ul>
  );
}

function ChannelPostRow({
  post,
  timeZone,
}: {
  readonly post: ChannelMessage;
  readonly timeZone: string;
}) {
  return (
    <article className="channel-post">
      <header className="channel-post__head">
        <div className="channel-post__who">
          <span className="channel-post__author">{post.authorDisplayName}</span>
          <span className="channel-post__meta">{formatDisplayTime(post.postedAt, timeZone)}</span>
        </div>
      </header>
      <p className="channel-post__body">{post.body}</p>
    </article>
  );
}

function ChannelComposer({
  client,
  accountId,
  channel,
  onPosted,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly channel: Channel;
  readonly onPosted: () => void;
}) {
  const [draft, setDraft] = useState<ChannelAnnouncementDraft>(EMPTY_CHANNEL_ANNOUNCEMENT_DRAFT);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  function update(patch: Partial<ChannelAnnouncementDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const validation = validateChannelAnnouncement(draft);
    if (!validation.ok) {
      setFailure(validation.reason);
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await postChannelMessage(client, accountId, channel.channelId, draft);
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
      return;
    }
    setDraft(EMPTY_CHANNEL_ANNOUNCEMENT_DRAFT);
    setReceipt(`Posted to ${channel.name}.`);
    onPosted();
  }

  return (
    <form
      className="channel-composer"
      aria-label={`New message in ${channel.name}`}
      onSubmit={(event) => event.preventDefault()}
    >
      <label className="channel-form__field">
        <span className="channel-form__label">Message</span>
        <textarea
          rows={4}
          value={draft.body}
          disabled={busy}
          onChange={(event) => update({ body: event.target.value })}
        />
      </label>

      <p className="channel-form__note">
        Goes to everyone currently in {channel.name}. Membership is decided on the server.
      </p>

      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Posting..." : "Post message"}
        </button>
      </div>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}
